// src/components/Store/ShoppingCart.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from '../public/PublicLayout';

// ARCA Color Scheme
const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#2C5234',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40'
};

const ShoppingCart = ({ user, onLogout }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const navigate = useNavigate();

  // Sample promo codes
  const promoCodes = {
    'ARCA10': { discount: 0.10, type: 'percentage', description: '10% off your order' },
    'MEMBER20': { discount: 0.20, type: 'percentage', description: '20% member discount' },
    'SAVE25': { discount: 25, type: 'fixed', description: '$25 off your order' }
  };

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const savedCart = localStorage.getItem('arcaCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    setLoading(false);
  };

  const updateCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem('arcaCart', JSON.stringify(newCart));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(productId);
      return;
    }

    const newCart = cart.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    updateCart(newCart);
    toast.success('Quantity updated');
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    updateCart(newCart);
    toast.success('Item removed from cart');
  };

  const clearCart = () => {
    updateCart([]);
    setAppliedPromo(null);
    setPromoCode('');
    toast.success('Cart cleared');
  };

  const applyPromoCode = () => {
    const promo = promoCodes[promoCode.toUpperCase()];
    if (promo) {
      setAppliedPromo({ code: promoCode.toUpperCase(), ...promo });
      toast.success(`Promo code applied: ${promo.description}`);
    } else {
      toast.error('Invalid promo code');
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    toast.success('Promo code removed');
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    if (!appliedPromo) return 0;

    const subtotal = calculateSubtotal();
    if (appliedPromo.type === 'percentage') {
      return subtotal * appliedPromo.discount;
    } else {
      return Math.min(appliedPromo.discount, subtotal);
    }
  };

  const calculateShipping = () => {
    const subtotal = calculateSubtotal();
    // Free shipping over $100
    return subtotal >= 100 ? 0 : 15.99;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    return (subtotal - discount) * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const shipping = calculateShipping();
    const tax = calculateTax();
    return subtotal - discount + shipping + tax;
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading your cart...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="bg-light py-4">
        {/* Breadcrumb */}
        <div className="container py-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/store" style={{ color: ARCA_COLORS.primary }}>Store</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Shopping Cart</li>
            </ol>
          </nav>
        </div>

        {/* Main Content */}
        <div className="container py-4">
          <h1 className="h2 fw-bold mb-4" style={{ color: ARCA_COLORS.secondary }}>
            <i className="bi bi-cart3 me-2"></i>
            Shopping Cart
          </h1>

          {cart.length === 0 ? (
            /* Empty Cart */
            <div className="text-center py-5">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{ width: '80px', height: '80px', backgroundColor: ARCA_COLORS.light }}>
                <i className="bi bi-cart-x fs-1 text-muted"></i>
              </div>
              <h3 className="h5 fw-medium mb-2" style={{ color: ARCA_COLORS.secondary }}>Your cart is empty</h3>
              <p className="text-muted mb-4">Start shopping to add items to your cart.</p>
              <Link
                to="/store"
                className="btn btn-lg text-white fw-bold"
                style={{ backgroundColor: ARCA_COLORS.primary, borderColor: ARCA_COLORS.primary }}
              >
                <i className="bi bi-shop me-2"></i>
                Continue Shopping
              </Link>
            </div>
          ) : (
            /* Cart with Items */
            <div className="row g-4">
              {/* Cart Items */}
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm">
                  <div className="card-header" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 fw-bold">
                        <i className="bi bi-bag-check me-2"></i>
                        Cart Items ({getCartItemCount()})
                      </h5>
                      <button
                        onClick={clearCart}
                        className="btn btn-sm btn-light fw-medium"
                      >
                        <i className="bi bi-trash me-1"></i>
                        Clear Cart
                      </button>
                    </div>
                  </div>

                  <div className="card-body p-4">
                    <div className="list-group list-group-flush">
                      {cart.map((item) => (
                        <div key={item.id} className="list-group-item border-0 border-bottom py-3">
                          <div className="d-flex gap-3">
                            <div className="flex-shrink-0" style={{ width: '100px', height: '100px' }}>
                              <img
                                src={item.main_image?.startsWith('http') ? item.main_image : `${process.env.REACT_APP_BACKEND_URL || 'https://api.africaresp.org'}${item.main_image}` || '/api/placeholder/200/200'}
                                alt={item.name}
                                className="img-thumbnail w-100 h-100"
                                style={{ objectFit: 'cover' }}
                              />
                            </div>

                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="fw-bold mb-1">
                                  <Link
                                    to={`/store/product/${item.id}`}
                                    style={{ color: ARCA_COLORS.secondary, textDecoration: 'none' }}
                                    className="hover-primary"
                                  >
                                    {item.name}
                                  </Link>
                                </h6>
                                <h5 className="fw-bold mb-0" style={{ color: ARCA_COLORS.primary }}>
                                  ${(item.price * item.quantity).toFixed(2)}
                                </h5>
                              </div>
                              {item.category && <p className="text-muted small mb-1">{item.category}</p>}
                              {item.sku && <p className="text-muted small mb-2">SKU: {item.sku}</p>}

                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <div className="d-flex align-items-center gap-2">
                                  <label className="text-muted small mb-0">Qty:</label>
                                  <select
                                    value={item.quantity}
                                    onChange={(e) => updateQuantity(item.id, parseInt(e.target.value))}
                                    className="form-select form-select-sm"
                                    style={{ width: 'auto', borderColor: ARCA_COLORS.primary }}
                                  >
                                    {[...Array(Math.min(10, item.stock_quantity || 10))].map((_, i) => (
                                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                                    ))}
                                  </select>
                                  <span className="text-muted small">× ${item.price}</span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => removeItem(item.id)}
                                  className="btn btn-sm btn-outline-danger"
                                >
                                  <i className="bi bi-trash me-1"></i>
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Continue Shopping */}
                <div className="mt-3">
                  <Link
                    to="/store"
                    className="btn btn-outline-secondary"
                    style={{ borderColor: ARCA_COLORS.primary, color: ARCA_COLORS.primary }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Continue Shopping
                  </Link>
                </div>
              </div>

              {/* Order Summary */}
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                  <div className="card-header" style={{ backgroundColor: ARCA_COLORS.secondary, color: 'white' }}>
                    <h5 className="mb-0 fw-bold">
                      <i className="bi bi-receipt me-2"></i>
                      Order Summary
                    </h5>
                  </div>
                  <div className="card-body p-4">

                    {/* Promo Code */}
                    <div className="mb-4">
                      <label htmlFor="promo-code" className="form-label fw-medium">
                        <i className="bi bi-tag me-1"></i>
                        Promo Code
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          id="promo-code"
                          name="promo-code"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          className="form-control"
                          placeholder="Enter promo code"
                          disabled={!!appliedPromo}
                        />
                        {appliedPromo ? (
                          <button
                            type="button"
                            onClick={removePromoCode}
                            className="btn btn-danger"
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={applyPromoCode}
                            className="btn"
                            style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}
                          >
                            Apply
                          </button>
                        )}
                      </div>
                      {appliedPromo && (
                        <div className="alert alert-success mt-2 py-2 px-3 mb-0" role="alert">
                          <small><i className="bi bi-check-circle me-1"></i>{appliedPromo.description}</small>
                        </div>
                      )}
                    </div>

                    {/* Price Breakdown */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Subtotal</span>
                        <span className="fw-medium">${calculateSubtotal().toFixed(2)}</span>
                      </div>

                      {appliedPromo && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Discount ({appliedPromo.code})</span>
                          <span className="fw-medium" style={{ color: ARCA_COLORS.success }}>-${calculateDiscount().toFixed(2)}</span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Shipping</span>
                        <span className="fw-medium">
                          {calculateShipping() === 0 ? (
                            <span className="badge" style={{ backgroundColor: ARCA_COLORS.success }}>FREE</span>
                          ) : (
                            `$${calculateShipping().toFixed(2)}`
                          )}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between mb-3">
                        <span className="text-muted">Tax</span>
                        <span className="fw-medium">${calculateTax().toFixed(2)}</span>
                      </div>

                      <div className="border-top pt-3">
                        <div className="d-flex justify-content-between">
                          <span className="h5 fw-bold mb-0" style={{ color: ARCA_COLORS.secondary }}>Order Total</span>
                          <span className="h5 fw-bold mb-0" style={{ color: ARCA_COLORS.primary }}>${calculateTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {calculateSubtotal() < 100 && (
                      <div className="alert alert-info border-0 py-2 px-3" style={{ backgroundColor: '#e3f2fd' }}>
                        <small>
                          <i className="bi bi-truck me-1"></i>
                          Add ${(100 - calculateSubtotal()).toFixed(2)} more to qualify for free shipping!
                        </small>
                      </div>
                    )}

                    <Link
                      to="/checkout"
                      className="btn btn-lg w-100 text-white fw-bold mt-3"
                      style={{ backgroundColor: ARCA_COLORS.secondary, borderColor: ARCA_COLORS.secondary }}
                    >
                      <i className="bi bi-credit-card me-2"></i>
                      Proceed to Checkout
                    </Link>

                    <div className="text-center mt-3">
                      <small className="text-muted">
                        <i className="bi bi-shield-check me-1"></i>
                        Secure SSL encrypted transaction
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}        </div>
      </div>
    </PublicLayout>
  );
};

export default ShoppingCart;